import { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import Login from "./components/Login";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import PropTypes from "prop-types";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "./config/api";
import { ToastContainer } from "./components/ui/Toast";
import { canOpenHrPages, hrHomePath } from "./utils/hrAccess";
import { isMicRole, MIC_ALLOWED_PATHS } from "./utils/micAccess";
import "./styles/micReadOnly.css";

// Lazy load components for better performance
const Dashboard = lazy(() => import("./components/Dashboard"));
const Leads = lazy(() => import("./pages/Leads"));
const Clients = lazy(() => import("./pages/Clients"));
const Employees = lazy(() => import("./pages/Employees"));
const PropertyPage = lazy(() => import("./pages/PropertyPage"));
const Opportunity = lazy(() => import("./pages/opportunity"));
const Profile = lazy(() => import("./pages/profile"));
const Report = lazy(() => import("./pages/Report"));
const Updateopportunity = lazy(() => import("./pages/Updateopportunity"));
const Data = lazy(() => import("./components/data"));
const Test = lazy(() => import("./pages/test"));
const Newopp = lazy(() => import("./pages/Newopp"));
const Chat = lazy(() => import("./components/Chat"));
const RRport = lazy(() => import("./components/rrport"));
const ReportDaily = lazy(() => import("./pages/ReportDaily"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const TaskManagement = lazy(() => import("./pages/TaskManagement"));
const TodoManagement = lazy(() => import("./pages/TodoManagement"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const TargetAchievement = lazy(() => import("./pages/TargetAchievement"));
const AIInsights = lazy(() => import("./pages/AIInsights"));
const CityAdd = lazy(() => import("./pages/CityAdd"));
const PublicProposalPage = lazy(() => import("./PublicProposalPage"));
const HrDashboard = lazy(() => import("./pages/hr/HrDashboard"));
const HrEmployees = lazy(() => import("./pages/hr/HrEmployees"));
const HrEmployeeProfile = lazy(() => import("./pages/hr/HrEmployeeProfile"));
const HrAttendance = lazy(() => import("./pages/hr/HrAttendance"));
const HrLeave = lazy(() => import("./pages/hr/HrLeave"));
const HrPerformance = lazy(() => import("./pages/hr/HrPerformance"));
const HrLearning = lazy(() => import("./pages/hr/HrLearning"));
const HrRecruitment = lazy(() => import("./pages/hr/HrRecruitment"));
const HrSettings = lazy(() => import("./pages/hr/HrSettings"));

// Loading component for suspense fallback
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px",
      fontSize: "16px",
      color: "#666",
    }}
  >
    <div>Loading...</div>
  </div>
);

const MicShell = ({ children }) => {
  const location = useLocation();
  const mic = isMicRole();

  useEffect(() => {
    if (mic) {
      document.documentElement.dataset.crmRole = "MIC";
    } else {
      delete document.documentElement.dataset.crmRole;
    }
    return () => {
      delete document.documentElement.dataset.crmRole;
    };
  }, [mic]);

  if (mic) {
    const path = location.pathname;
    const allowed = MIC_ALLOWED_PATHS.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

MicShell.propTypes = {
  children: PropTypes.node.isRequired,
};

const AppLayout = ({ children }) => (
  <>
    <Navbar />
    <div className="layout">
      <Sidebar />
      <div className="content">
        <MicShell>
          {isMicRole() ? (
            <div
              className="mic-readonly-banner"
              role="status"
              style={{
                margin: "0 0 12px",
                padding: "8px 12px",
                borderRadius: 8,
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                color: "#065f46",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              MIC view — read-only. Contact details (phone/email) are hidden.
              Create, edit, and assign are disabled.
            </div>
          ) : null}
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
          </ErrorBoundary>
        </MicShell>
      </div>
    </div>
  </>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem("authToken") ? true : false;
  return isLoggedIn ? children : <Navigate to="/" />;
};

const SuperAdminRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem("authToken") ? true : false;
  const role = localStorage.getItem("userRole");
  if (!isLoggedIn) return <Navigate to="/" />;
  if (role !== "Super Admin") return <Navigate to="/dashboard" replace />;
  return children;
};

const HrOpsRoute = ({ children }) => {
  const isLoggedIn = Boolean(localStorage.getItem("authToken"));
  const role = localStorage.getItem("userRole");
  if (!isLoggedIn) return <Navigate to="/" />;
  if (!canOpenHrPages(role)) return <Navigate to={hrHomePath(role)} replace />;
  return children;
};

const HomeDashboard = () => {
  const role = localStorage.getItem("userRole");
  if (role === "HR") return <Navigate to="/hr" replace />;
  if (role === "Employee") return <Navigate to="/profile" replace />;
  return <Dashboard />;
};

SuperAdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

HrOpsRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Main App Component
const App = () => {
  const isLoggedIn = localStorage.getItem("authToken") ? true : false;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ErrorBoundary>
        <Router>
          <Routes>
            {/* Redirect to dashboard if logged in */}
            <Route
              path="/"
              element={isLoggedIn ? <Navigate to="/dashboard" /> : <Login />}
            />
            <Route
              path="/login"
              element={isLoggedIn ? <Navigate to="/dashboard" /> : <Login />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HomeDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Leads />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Employees />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Clients />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PropertyPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Chat />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rrport"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RRport />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report-daily"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ReportDaily />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/pipeline"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Pipeline />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/opportunity"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Opportunity />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/newopp"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Newopp />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Report />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/updateopportunity"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Updateopportunity />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Profile />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/data"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Data />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/test"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Test />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/todos"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TodoManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AuditLogs />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/target-achievement"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TargetAchievement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-insights"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIInsights />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cityadd"
              element={
                <SuperAdminRoute>
                  <AppLayout>
                    <CityAdd />
                  </AppLayout>
                </SuperAdminRoute>
              }
            />
            <Route
              path="/hr"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrDashboard />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/me"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrEmployeeProfile self />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/employees"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrEmployees />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/employees/:id"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrEmployeeProfile />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/attendance"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrAttendance />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/leave"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrLeave />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/performance"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrPerformance />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/learning"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrLearning />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/recruitment"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrRecruitment />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/hr/settings"
              element={
                <HrOpsRoute>
                  <AppLayout>
                    <HrSettings />
                  </AppLayout>
                </HrOpsRoute>
              }
            />
            <Route
              path="/proposal/public/:opportunityId"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <PublicProposalPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </Routes>
        </Router>
      </ErrorBoundary>
      <ToastContainer position="top-right" maxToasts={5} />
    </GoogleOAuthProvider>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default App;
